import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowClockwise, Clock, Lightning, Package, Tag, TrendDown, TrendUp, Warning } from 'shared/icons';
import CampaignApplyNow from '../components/campaigns/CampaignApplyNow';
import CampaignMonitorItems from '../components/campaigns/CampaignMonitorItems';
import CampaignOverviewSummary from '../components/campaigns/CampaignOverviewSummary';
import { CampaignBonusesPanel, CampaignItemEventsPanel } from '../components/campaigns/CampaignAuditPanel'; // Novo componente
import LegacyByCampaign from '../components/campaigns/legacy/LegacyByCampaign';
import LegacyReports from '../components/campaigns/legacy/LegacyReports';
import LegacySuggestions from '../components/campaigns/legacy/LegacySuggestions';
import { LegacyInactiveToggle } from '../components/campaigns/legacy/legacyShared';
import PromotionsList from '../components/campaigns/PromotionsList';
import RebateOpportunities from '../components/campaigns/RebateOpportunities';
import { GetCampaignTypeLabel } from '../constants/campaignTypes';
import PageHeader from '../components/layout/PageHeader';
// Mesmos mapas do card do Monitor Unificado: a aba Atenção traduz o
// `attention_status` e o `ml_status` com os MESMOS rótulos do card, para o
// usuário não ver dois nomes para o mesmo estado (CLAUDE.md §4).
import { ATTENTION_LABELS, FormatIntOrDash, ML_STATUS_BADGES, Pill } from '../components/campaigns/campaignCardShared';
import Button from '../components/ui/Button';
import ErrorCard from '../components/ui/ErrorCard';
import StandardTable from '../components/ui/StandardTable';
import api from '../services/api';
import { FormatIsoDate } from '../utils/date';
import { FormatPrice } from '../utils/format';

// Navegação em 2 níveis: grupos (nível 1) → sub-abas (nível 2).
// `activeTab` guarda SEMPRE o id da sub-aba (overview, attention, types, items,
// campaigns, legacy-*) e o grupo ativo é derivado dele.
// A antiga sub-aba `by-item` (busca por anúncio) foi ABSORVIDA pelo input de busca
// do próprio Monitor Unificado (param `search`, server-side) — o grupo "Unificado"
// tem uma sub-aba só, então o nível 2 não é renderizado nele.
const TAB_GROUPS = [
    {
        id: 'overview',
        label: 'Visão geral',
        icon: TrendDown,
        tabs: [{ id: 'overview', label: 'Estatisticas Gerais', icon: TrendDown }],
    },
    {
        id: 'attention',
        label: 'Atenção',
        icon: Warning,
        tabs: [{ id: 'attention', label: 'Atencao', icon: Warning }],
    },
    {
        id: 'campaigns',
        label: 'Campanhas',
        icon: Tag,
        tabs: [
            { id: 'types', label: 'Tipos de Campanhas', icon: Tag },
            { id: 'campaigns', label: 'Todas as Campanhas', icon: Tag },
        ],
    },
    {
        // Grupo de UMA sub-aba: a busca virou input dentro do próprio monitor
        // (param `search`, server-side), então não há linha de sub-abas aqui.
        id: 'items',
        label: 'Unificado',
        icon: Package,
        tabs: [{ id: 'items', label: 'Monitor Unificado', icon: Package }],
    },
    {
        // Fila de trabalho (22/09): a troca com VANTAGEM EXPLÍCITA e nenhum
        // prejuízo — líquido maior sem o cliente pagar mais, ou cliente pagando
        // menos sem o líquido cair (a régua do dono; exigir melhora estrita dos
        // dois lados descartava o caso mais comum). A régua mora no backend
        // (`backend_v2/src/modules/campaigns/applyNowRule.js`), que marca cada
        // campanha; o card da Visão geral conta a mesma fila. O Radar de Rebate
        // mostra DE PROPÓSITO as trocas que derrubam o líquido; aqui a pergunta
        // é "o que eu aplico hoje sem pensar duas vezes?", ordenada por impacto.
        id: 'apply-now',
        label: 'Aplique agora',
        icon: Lightning,
        tabs: [{ id: 'apply-now', label: 'Aplique agora', icon: Lightning }],
    },
    {
        // Auditoria (20/08): a linha do tempo das mudanças de campanha por
        // anúncio (com a divergência de opt-in que o dono pediu) e os bônus
        // (cashback) que o ML paga por vender — direto do extrato do MP.
        id: 'audit',
        label: 'Auditoria',
        icon: Warning,
        tabs: [
            // Radar de rebate: a lente que a aba "Melhores Campanhas" não dá,
            // porque lá o Pareto exige não piorar líquido NEM consumidor e
            // esconde justamente a troca que paga mais rebate.
            { id: 'audit-rebate', label: 'Radar de Rebate', icon: TrendUp },
            { id: 'audit-events', label: 'Mudanças & Divergências', icon: Warning },
            { id: 'audit-bonuses', label: 'Bônus (cashback)', icon: Tag },
        ],
    },
    {
        // Abas Legacy = cópia do Campaign Manager (meli_manager) da diretoria.
        // "Por Anúncio" e "Anúncios" foram REMOVIDAS (2026-07-30): o Monitor
        // Unificado absorveu as duas leituras — busca por item e listagem com
        // filtros/contagens — com dados próprios do MySQL em vez do Postgres.
        id: 'legacy',
        label: 'Legacy',
        icon: Clock,
        tabs: [
            { id: 'legacy-by-campaign', label: 'Por Campanha', icon: Tag },
            { id: 'legacy-suggestions', label: 'Melhores Campanhas', icon: TrendDown },
            { id: 'legacy-reports', label: 'Relatórios', icon: Warning },
        ],
    },
];

// Abas servidas pelo `loadData` (API antiga de campanhas). O guard do fetch, a
// página de erro, o skeleton e o botão Atualizar do header derivam TODOS desta
// lista — assim nenhum tratamento cobre uma aba que o fetch não atende.
const LEGACY_DATA_TABS = ['overview', 'attention', 'types'];

// Ids de TODAS as sub-abas: valida o deep-link `?tab=` — id desconhecido cai
// na aba padrão em vez de renderizar página vazia.
const ALL_TAB_IDS = new Set(TAB_GROUPS.flatMap((group) => group.tabs.map((tab) => tab.id)));

const Campaigns = () => {
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [needsAttention, setNeedsAttention] = useState([]);
    const [campaignTypes, setCampaignTypes] = useState([]);
    // F25: participação automática em campanhas (lista de exclusão do ML).
    // null = ainda não consultado / não verificável; o selo nunca inventa "ativa".
    const [exclusion, setExclusion] = useState(null);
    useEffect(() => {
        let alive = true;
        api.getCampaignExclusionStatus()
            .then((data) => alive && setExclusion(data))
            .catch(() => alive && setExclusion({ excluded: null, error: 'falha' }));
        return () => {
            alive = false;
        };
    }, []);
    // Aba padrão = Monitor Unificado; `?tab=` permite chegar direto numa aba
    // específica (o card "Precisam de atenção" do Briefing, que agora mora na
    // página Inteligência de Mercado, aponta para /campaigns?tab=attention).
    const [activeTab, setActiveTab] = useState(() => {
        const requested = new URLSearchParams(location.search).get('tab');
        return ALL_TAB_IDS.has(requested) ? requested : 'items';
    });
    // Presets de navegação vindos dos cards da Visão geral: o card "Rebate" abre
    // o Monitor já filtrado "Com rebate", o de campanhas abre a lista nas em
    // andamento. Valem só para a PRÓXIMA montagem da aba de destino (os
    // componentes os leem no `useState` inicial) e qualquer troca de aba pelas
    // abas, ou pelo `?tab=` da URL, os limpa — voltar ao Monitor pela aba é
    // voltar ao Monitor padrão, não ao último recorte de um card.
    const [monitorPreset, setMonitorPreset] = useState(null);
    const [campaignsStatusPreset, setCampaignsStatusPreset] = useState(null);
    // Pedido de recarga do resumo de hoje (Atualizar do header). O resumo fica
    // montado enquanto a aba é a Visão geral — o skeleton do `loadData` não o
    // desmonta —, então recarregar é pedido explícito, não efeito colateral.
    const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);

    // Navegação interna entre abas, com preset opcional. NÃO usa
    // navigate('/campaigns?tab=…'): a aba trocada pelo clique nunca morou na
    // URL, e pôr ali um `?tab=` faria o efeito abaixo rodar e limpar o preset.
    // Um deep-link de notificação (`?item_id=…`) que ainda esteja na URL não
    // soma com o preset: quem resolve é o Monitor, que na montagem com preset
    // ignora o deep-link já presente (ver `CampaignMonitorItems`). Limpar a URL
    // daqui não bastaria — com `v7_startTransition` a troca de location chega
    // num render POSTERIOR ao da montagem do Monitor.
    const goToTab = useCallback(({ tab, monitorFilters = null, campaignsStatus = null }) => {
        setMonitorPreset(monitorFilters);
        setCampaignsStatusPreset(campaignsStatus);
        setActiveTab(tab);
    }, []);

    // A URL pode mudar com a página já montada (clique numa notificação estando
    // em /campaigns noutra aba): a aba precisa seguir o `?tab=` novo. Sem
    // `tab` na URL nada muda — trocar aba pelo clique não mexe na URL. Passa por
    // `goToTab` para que um preset antigo de card não sobreviva ao deep-link.
    useEffect(() => {
        const requested = new URLSearchParams(location.search).get('tab');
        if (requested && ALL_TAB_IDS.has(requested)) {
            goToTab({ tab: requested });
        }
    }, [location.search, goToTab]);
    // Toggle global das abas Legacy (mesma semântica do app antigo).
    const [includeInactive, setIncludeInactive] = useState(false);

    // Grupo ativo derivado da sub-aba ativa (ids antigos de activeTab seguem
    // selecionando o grupo + sub-aba correspondentes).
    const activeGroup = TAB_GROUPS.find((group) => group.tabs.some((tab) => tab.id === activeTab)) ?? TAB_GROUPS[0];

    const loadData = useCallback(async () => {
        // Carregar dados legados apenas para as abas que usam a API antiga
        if (!LEGACY_DATA_TABS.includes(activeTab)) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const [statsRes, attentionRes, typesRes] = await Promise.all([
                api.get('/campaigns/overview/stats'),
                api.get('/campaigns/items/needs-attention'),
                api.get('/campaigns/types'),
            ]);

            const statsPayload = statsRes?.data ?? statsRes;
            const attentionPayload = attentionRes?.data ?? attentionRes;
            const typesPayload = typesRes?.data ?? typesRes;

            setStats(statsPayload || null);
            setNeedsAttention(
                Array.isArray(attentionPayload)
                    ? attentionPayload
                    : Array.isArray(attentionPayload?.items)
                      ? attentionPayload.items
                      : [],
            );
            setCampaignTypes(
                Array.isArray(typesPayload) ? typesPayload : Array.isArray(typesPayload?.data) ? typesPayload.data : [],
            );
        } catch (err) {
            setError({
                title: 'Erro ao Carregar Dados',
                message: err.message || 'Nao foi possivel carregar os dados das campanhas.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        loadData();
    }, [loadData]); // Dependency array updated to include loadData

    // Atualizar do header: o bloco antigo (`loadData`) e o resumo de hoje, que
    // tem fetch próprio. Só o botão pede o resumo — a troca de aba não, senão
    // cada visita à Visão geral buscaria o resumo duas vezes.
    const refreshFromHeader = () => {
        setSummaryRefreshKey((key) => key + 1);
        loadData();
    };

    // O badge do grupo "Atenção" (nível 1) fica visível em qualquer aba, mas o
    // `loadData` só roda nas abas legadas — quem vive no Monitor Unificado (aba
    // padrão) precisa da contagem carregada uma vez no mount. Visitar as abas
    // legadas continua atualizando a lista via `loadData`.
    useEffect(() => {
        let cancelled = false;
        const loadAttentionBadge = async () => {
            try {
                const response = await api.get('/campaigns/items/needs-attention');
                const payload = response?.data ?? response;
                const items = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : [];
                if (!cancelled) {
                    setNeedsAttention(items);
                }
            } catch {
                // Badge é informativo: sem contagem ele simplesmente não
                // aparece; a aba Atenção trata o erro no próprio loadData.
            }
        };
        loadAttentionBadge();
        return () => {
            cancelled = true;
        };
    }, []);

    const formatCurrency = (value) => FormatPrice(value);

    // F27: rótulo único do módulo (constants/campaignTypes), alinhado à doc do ML.
    const getCampaignTypeLabel = (type) => GetCampaignTypeLabel(type, type || '-');

    // O diagnóstico já vem pronto do backend em `attention_status` (slug) —
    // aqui só se traduz; recalcular o problema a partir de preço/data no
    // frontend divergiria da regra que marcou a linha.
    const resolveAttentionProblem = (item) => ATTENTION_LABELS[item.attention_status] || item.attention_status || '-';

    const attentionColumns = [
        {
            key: 'item',
            label: 'Item',
            getValue: (row) => row.title || row.item_id || '-',
            render: (row) => (
                <div>
                    <p className="font-medium text-black dark:text-white">{row.title || row.item_id}</p>
                    {row.sku ? <p className="text-xs text-mono-500">SKU: {row.sku}</p> : null}
                </div>
            ),
        },
        {
            key: 'campaign',
            label: 'Campanha',
            getValue: (row) => row.campaign_name || '-',
            render: (row) => (
                <div>
                    <p className="text-sm text-black dark:text-white">{row.campaign_name || '-'}</p>
                    <p className="text-xs text-mono-500">{getCampaignTypeLabel(row.campaign_type)}</p>
                </div>
            ),
        },
        {
            // Status do ANÚNCIO no ML (active/paused/...): o problema em si já
            // tem coluna própria ('Problema'), então aqui entra o contexto que
            // falta — se o anúncio está no ar ou não.
            key: 'ml_status',
            label: 'Status',
            align: 'center',
            sortable: false,
            getValue: (row) => ML_STATUS_BADGES[row.ml_status]?.label || row.ml_status || '-',
            render: (row) => {
                const badge = ML_STATUS_BADGES[row.ml_status];
                if (!badge) {
                    return <span className="text-xs text-mono-500">{row.ml_status || '-'}</span>;
                }
                return <Pill tone={badge.tone}>{badge.label}</Pill>;
            },
        },
        {
            key: 'item_price',
            label: 'Preco',
            align: 'center',
            searchable: false,
            getValue: (row) => formatCurrency(row.item_price),
        },
        {
            key: 'stock_bling',
            label: 'Bling Stock',
            align: 'center',
            searchable: false,
            getValue: (row) => row.stock_bling ?? '-',
        },
        {
            key: 'problem',
            label: 'Problema',
            sortable: false,
            getValue: (row) => resolveAttentionProblem(row),
            render: (row) => (
                <span className="rounded bg-red-100 px-2 py-1 text-xs text-red-800 dark:bg-red-900 dark:text-red-200">
                    {resolveAttentionProblem(row)}
                </span>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <PageHeader
                title="Análise Meli"
                subtitle="Monitoramento e performance de campanhas e promoções"
                actions={
                    // Um só `actions`: com a prop duplicada o JSX fica com a
                    // última e o selo de exclusão (F25) nunca aparecia.
                    <div className="flex items-center gap-3">
                        <ExclusionBadge exclusion={exclusion} />
                        {/* O Atualizar do header dispara o `loadData` legado, então
                            só existe nas abas que ele atende — nas demais cada
                            painel (monitor, auditoria, legacy) tem o próprio refresh
                            e um botão morto aqui só confundiria. */}
                        {LEGACY_DATA_TABS.includes(activeTab) ? (
                            <Button onClick={refreshFromHeader} size="sm" disabled={loading}>
                                <ArrowClockwise size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                                Atualizar
                            </Button>
                        ) : null}
                    </div>
                }
            />

            {/* Tabs — nível 1: grupos; nível 2: sub-abas (pills) do grupo ativo */}
            <div className="space-y-3">
                <div className="border-b border-mono-200 dark:border-mono-800">
                    <nav className="flex space-x-8 overflow-x-auto">
                        {TAB_GROUPS.map((group) => {
                            const Icon = group.icon;
                            const isActiveGroup = group.id === activeGroup.id;
                            return (
                                <button
                                    type="button"
                                    key={group.id}
                                    onClick={() => goToTab({ tab: group.tabs[0].id })}
                                    aria-current={isActiveGroup ? 'true' : undefined}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-2 ${
                                        isActiveGroup
                                            ? 'border-black dark:border-white text-black dark:text-white'
                                            : 'border-transparent text-mono-500 hover:text-mono-700 dark:hover:text-mono-300 hover:border-mono-300'
                                    }`}
                                >
                                    <Icon size={18} />
                                    {group.label}
                                    {group.id === 'legacy' && (
                                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
                                            Campaign Manager
                                        </span>
                                    )}
                                    {group.id === 'attention' && needsAttention.length > 0 && (
                                        <span className="ml-2 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 px-2 py-0.5 rounded-full text-xs">
                                            {needsAttention.length}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Nível 2: só aparece quando o grupo ativo tem mais de uma sub-aba */}
                {activeGroup.tabs.length > 1 && (
                    <nav className="flex flex-wrap items-center gap-2">
                        {activeGroup.tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActiveTab = activeTab === tab.id;
                            return (
                                <button
                                    type="button"
                                    key={tab.id}
                                    onClick={() => goToTab({ tab: tab.id })}
                                    aria-current={isActiveTab ? 'true' : undefined}
                                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                                        isActiveTab
                                            ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
                                            : 'border-mono-200 bg-white text-mono-600 hover:border-mono-300 hover:text-mono-900 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400 dark:hover:border-mono-700 dark:hover:text-mono-200'
                                    }`}
                                >
                                    <Icon size={14} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                )}
            </div>

            {/* Resumo de hoje (29/09): os quatro números do estado ATUAL dos
                anúncios ativos, cada um levando à tela que o detalha. Fica FORA
                do ternário de erro/skeleton do `loadData`: tem fetch, carregando
                e erro próprios, então uma falha do `/overview/stats` (ou das
                outras duas chamadas do `loadData`) não o esconde, e o skeleton
                não o desmonta — desmontar e remontar jogava fora a primeira
                busca e fazia outra. O Atualizar do header o recarrega pelo
                `refreshKey`. */}
            {activeTab === 'overview' && (
                <CampaignOverviewSummary onNavigate={goToTab} refreshKey={summaryRefreshKey} />
            )}

            {/* Falha do `loadData` troca só o conteúdo das abas que ele atende
                — o header, as abas e o resumo de hoje continuam na tela. */}
            {error && LEGACY_DATA_TABS.includes(activeTab) ? (
                <ErrorCard
                    title={error.title}
                    message={error.message}
                    action="Tentar Novamente"
                    actionFn={loadData}
                    type={error.type}
                />
            ) : loading && LEGACY_DATA_TABS.includes(activeTab) ? (
                <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                        <div
                            key={i}
                            className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6"
                        >
                            <div className="h-6 w-64 bg-mono-200 dark:bg-mono-800 rounded animate-pulse mb-4" />
                            <div className="h-4 w-full bg-mono-200 dark:bg-mono-800 rounded animate-pulse" />
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    {/* Abas Legacy — cópia do Campaign Manager (meli_manager) da diretoria.
                        Toggle global "Incluir inativos" compartilhado, como no app antigo. */}
                    {activeTab.startsWith('legacy-') && activeTab !== 'legacy-reports' && (
                        <div className="-mt-2">
                            <LegacyInactiveToggle includeInactive={includeInactive} onChange={setIncludeInactive} />
                        </div>
                    )}
                    {activeTab === 'legacy-by-campaign' && <LegacyByCampaign includeInactive={includeInactive} />}
                    {activeTab === 'legacy-suggestions' && <LegacySuggestions includeInactive={includeInactive} />}
                    {activeTab === 'legacy-reports' && <LegacyReports />}

                    {/* Auditoria (20/08) */}
                    {activeTab === 'audit-rebate' && <RebateOpportunities />}
                    {activeTab === 'audit-events' && <CampaignItemEventsPanel />}
                    {activeTab === 'audit-bonuses' && <CampaignBonusesPanel />}

                    {/* Aplique agora (22/09): trocas com vantagem explícita e sem prejuízo */}
                    {activeTab === 'apply-now' && <CampaignApplyNow />}

                    {/* Items Tab (Monitor Unificado - NOVO). O preset de um card
                        da Visão geral só é lido na montagem. */}
                    {activeTab === 'items' && (
                        <div className="-mt-6">
                            <CampaignMonitorItems initialFilters={monitorPreset} />
                        </div>
                    )}

                    {/* Campaigns Tab */}
                    {activeTab === 'campaigns' && <PromotionsList initialStatus={campaignsStatusPreset} />}

                    {/* Overview Tab (Legacy/Stats) */}
                    {activeTab === 'overview' && stats && (
                        <div className="space-y-6">
                            {/* Stats Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-mono-600 dark:text-mono-400">
                                                Total de Campanhas
                                            </p>
                                            <p className="text-3xl font-bold text-black dark:text-white mt-2">
                                                {stats.total_campaigns || 0}
                                            </p>
                                            <p className="text-xs text-mono-500 mt-1">
                                                {stats.active_campaigns || 0} ativas
                                            </p>
                                        </div>
                                        <Tag size={32} className="text-mono-400" />
                                    </div>
                                </div>

                                {/* Rótulo honesto (29/09): o número são anúncios DISTINTOS com
                                    participação viva (iniciada, programada, pendente ou convite),
                                    e o de baixo são PARTICIPAÇÕES em qualquer status — um anúncio
                                    em 5 campanhas conta 5. O antigo "X de Y total" comparava as
                                    duas unidades como se fossem a mesma. */}
                                <div
                                    className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6"
                                    title="Anúncios distintos com participação iniciada, programada, pendente ou convite do ML (inclui anúncios pausados). As participações contam uma por campanha."
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-mono-600 dark:text-mono-400">
                                                Anúncios com participação
                                            </p>
                                            <p className="text-3xl font-bold text-black dark:text-white mt-2">
                                                {FormatIntOrDash(stats.items_with_active_promotion)}
                                            </p>
                                            <p className="text-xs text-mono-500 mt-1">
                                                {FormatIntOrDash(stats.total_items)} participações somando todas
                                            </p>
                                        </div>
                                        <Package size={32} className="text-mono-400" />
                                    </div>
                                </div>
                                {/* "Preco Medio Promocional" e "Desconto Total" saíram (29/09): o
                                    backend os devolvia como 0 fixo, e o card mostrava R$ 0,00
                                    como se fosse medido. O rebate por unidade do resumo acima é
                                    o número de dinheiro desta aba. */}
                            </div>

                            {/* Status Breakdown */}
                            <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                                <h2 className="text-xl font-bold text-black dark:text-white mb-4">
                                    Status das Campanhas
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                        <p className="text-sm text-mono-600 dark:text-mono-400">Ativas</p>
                                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                            {stats.active_campaigns || 0}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                                        {/* Campanha `pending` = programada: o ML já publicou e ela
                                            ainda não começou. "Candidatas" contava `candidate`, que é
                                            status de PARTICIPAÇÃO (convite), não de campanha — na
                                            base saía sempre 0. */}
                                        <p className="text-sm text-mono-600 dark:text-mono-400">Programadas</p>
                                        <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                            {FormatIntOrDash(stats.pending_campaigns)}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg">
                                        <p className="text-sm text-mono-600 dark:text-mono-400">Finalizadas</p>
                                        <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                                            {stats.finished_campaigns || 0}
                                        </p>
                                    </div>
                                    {/* As fantasmas (29/09): "Ativas" passou a ser em andamento
                                        HOJE, e as campanhas que o ML ainda marca como iniciadas
                                        mas estão vencidas ou sem nenhum anúncio participando
                                        saíram dela. Sem este balde elas sumiam na diferença do
                                        total e a conta dos quatro não fechava. */}
                                    <div
                                        className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg"
                                        title="Campanhas que o ML ainda marca como iniciadas, mas já passaram do fim ou não têm nenhum anúncio participando. O sync não encerra campanha que sumiu do ML."
                                    >
                                        <p className="text-sm text-mono-600 dark:text-mono-400">Sem andamento</p>
                                        <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                            {FormatIntOrDash(stats.stale_campaigns)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Items Status */}
                            <div className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-xl p-6">
                                {/* Contagens de PARTICIPAÇÃO (linha de campaign_items), não de
                                    anúncio: um anúncio convidado para 20 campanhas conta 20. */}
                                <h2 className="text-xl font-bold text-black dark:text-white mb-4">
                                    Status das Participações
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                        <p className="text-sm text-mono-600 dark:text-mono-400">Iniciadas</p>
                                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                            {stats.items_started || 0}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                                        <p className="text-sm text-mono-600 dark:text-mono-400">Candidatas</p>
                                        <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                            {stats.items_candidate || 0}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                        <p className="text-sm text-mono-600 dark:text-mono-400">
                                            Anúncios que precisam de atenção
                                        </p>
                                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                                            {needsAttention.length}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tipos de Campanhas Tab */}
                    {activeTab === 'types' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {campaignTypes.map((type, idx) => (
                                <div
                                    key={idx}
                                    className="bg-white dark:bg-mono-950 border border-mono-200 dark:border-mono-800 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-blue-600 dark:text-blue-400">
                                            <Tag size={24} weight="fill" />
                                        </div>
                                        <span className="text-xs font-bold bg-mono-100 dark:bg-mono-800 text-mono-600 dark:text-mono-400 px-2 py-1 rounded-lg">
                                            {type.campaign_count} campanhas
                                        </span>
                                    </div>

                                    <h3 className="text-lg font-bold text-mono-900 dark:text-mono-100 mb-2 capitalize">
                                        {getCampaignTypeLabel(type.type)}
                                    </h3>

                                    <div className="space-y-3 mt-4">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-mono-500">Itens Ativos:</span>
                                            <span className="font-bold text-green-600 dark:text-green-400">
                                                {type.active_items || 0}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-mono-500">Itens Programados:</span>
                                            <span className="font-bold text-blue-600 dark:text-blue-400">
                                                {type.scheduled_items || 0}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm border-t border-mono-100 dark:border-mono-800 pt-2 mt-2">
                                            <span className="text-mono-500 font-medium">Total de Itens:</span>
                                            <span className="font-bold text-mono-900 dark:text-mono-100">
                                                {type.total_items || 0}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-6 pt-4 border-t border-mono-50 dark:border-mono-900 flex justify-between items-center text-[10px] text-mono-400 uppercase tracking-wider font-bold">
                                        <span>{FormatIsoDate(type.earliest_start, { blank: '-' })}</span>
                                        <span className="mx-2">ate</span>
                                        <span>{FormatIsoDate(type.latest_finish, { blank: '-' })}</span>
                                    </div>
                                </div>
                            ))}
                            {campaignTypes.length === 0 && !loading && (
                                <div className="col-span-full py-12 text-center bg-mono-50 dark:bg-mono-900/20 rounded-2xl border border-dashed border-mono-200 dark:border-mono-800">
                                    <p className="text-mono-500">Nenhum dado de tipo de campanha disponivel.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Attention Tab */}
                    {activeTab === 'attention' && (
                        <div className="space-y-4">
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                                <div className="flex items-center gap-2">
                                    <Warning size={20} className="text-yellow-600 dark:text-yellow-400" />
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                        {needsAttention.length} itens precisam de atencao. Verifique os status, precos e
                                        datas.
                                    </p>
                                </div>
                            </div>

                            <StandardTable
                                rows={needsAttention}
                                columns={attentionColumns}
                                // `campaign_competition` é única por (org, item), então
                                // item_id identifica a linha sozinho — chave com índice
                                // remontaria as linhas quando a tabela reordena.
                                rowKey={(row) => row.item_id}
                                countLabel="itens"
                                emptyMessage="Nenhum item precisa de atencao no momento!"
                                searchPlaceholder="Buscar item, campanha ou problema..."
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

/**
 * F25 — Selo de participação automática em campanhas cofinanciadas.
 * Sem isso, "nenhuma campanha automática" era indistinguível de "conta excluída
 * de propósito". Três estados: ativa, excluída, e não verificado (nunca inventa).
 */
const ExclusionBadge = ({ exclusion }) => {
    if (!exclusion) return null;
    if (exclusion.excluded === true) {
        return (
            <span
                className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200"
                title="A conta está na lista de exclusão: o Mercado Livre não cria campanhas automáticas para ela"
            >
                Participação automática: excluída
            </span>
        );
    }
    if (exclusion.excluded === false) {
        return (
            <span
                className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200"
                title="A conta participa das campanhas automáticas (cofinanciada automatizada, preços competitivos)"
            >
                Participação automática: ativa
            </span>
        );
    }
    return (
        <span
            className="rounded-full bg-mono-100 px-3 py-1 text-xs font-medium text-mono-600 dark:bg-mono-800 dark:text-mono-300"
            title={exclusion.error ? `Não foi possível consultar: ${exclusion.error}` : 'Não verificado'}
        >
            Participação automática: não verificada
        </span>
    );
};

export default Campaigns;
